import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { colorOf, DAYS } from "@/lib/subjects";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, Target, BookMarked, ChevronLeft, ChevronRight, CheckCircle2, Circle, Sparkles } from "lucide-react";

const normalizeDays = (savedDays) => {
  const map = {};
  (savedDays || []).forEach((d) => { map[d.day] = d; });
  return DAYS.map((dayName) => map[dayName] || { day: dayName, date: "", label: "", entries: [] });
};

export default function WeeklyPlan() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [week, setWeek] = useState(1);
  const [weeks, setWeeks] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (studentId) return;
    api.get("/my/context").then(({ data }) => {
      setStudentId(data.student_id);
      setStudentName(data.student_name || "");
    });
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return;
    api.get("/weekly-plans/weeks", { params: { student_id: studentId } }).then(({ data }) => {
      setWeeks(data);
      if (data.length && !data.find((w) => w.week_number === week)) setWeek(data[0].week_number);
    });
  }, [studentId]);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/weekly-plans", { params: { student_id: studentId, week_number: week } });
      setPlan(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [studentId, week]);

  useEffect(() => {
    load();
  }, [load]);

  if (!studentId) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
        Bu hesaba bağlı bir öğrenci bulunamadı.
      </div>
    );
  }

  const totalTarget = plan?.days?.reduce((s, d) => s + d.entries.reduce((a, e) => a + (e.question_count || 0), 0), 0) || 0;
  const totalDone = plan?.days?.reduce((s, d) => s + d.entries.filter((e) => e.completed).reduce((a, e) => a + (e.question_count || 0), 0), 0) || 0;
  const pct = totalTarget ? Math.round((totalDone / totalTarget) * 100) : 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="animate-pop-in rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-sky-500 p-6 sm:p-8 text-white shadow-xl shadow-indigo-200">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold">
              <CalendarDays className="h-4 w-4" /> Haftalık Program
            </div>
            <h1 className="mt-3 font-display text-4xl font-extrabold">{plan?.month ? `${plan.month} · ` : ""}{week}. Hafta</h1>
            {studentName && <p className="mt-1 text-white/85">{studentName}{plan?.date_range ? ` · ${plan.date_range}` : ""}</p>}
            {plan?.week_label && <p className="text-white/70 text-sm">{plan.week_label}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="prev-week-btn"
              onClick={() => setWeek((w) => Math.max(1, w - 1))}
              className="rounded-xl bg-white/15 p-2.5 transition hover:bg-white/25 active:scale-95"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <select
              data-testid="week-select"
              value={week}
              onChange={(e) => setWeek(Number(e.target.value))}
              className="rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold outline-none [&>option]:text-slate-800"
            >
              {(weeks.length ? weeks : [{ week_number: week, month: "" }]).map((w) => (
                <option key={w.week_number} value={w.week_number}>{w.month ? `${w.month} · ` : ""}{w.week_number}. Hafta</option>
              ))}
            </select>
            <button
              data-testid="next-week-btn"
              onClick={() => setWeek((w) => w + 1)}
              className="rounded-xl bg-white/15 p-2.5 transition hover:bg-white/25 active:scale-95"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {plan && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-white/10 p-4">
              <div className="flex items-center gap-2 text-white/80 text-xs"><Target className="h-4 w-4" /> Haftalık Hedef</div>
              <div className="mt-1 text-2xl font-extrabold">{plan.weekly_target || totalTarget} soru</div>
            </div>
            <div className="rounded-2xl bg-white/10 p-4">
              <div className="flex items-center gap-2 text-white/80 text-xs"><CheckCircle2 className="h-4 w-4" /> Tamamlanan</div>
              <div className="mt-1 text-2xl font-extrabold">{totalDone} soru</div>
            </div>
            <div className="col-span-2 rounded-2xl bg-white/10 p-4 sm:col-span-1">
              <div className="flex items-center gap-2 text-white/80 text-xs"><Sparkles className="h-4 w-4" /> İlerleme</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="h-2.5 flex-1 rounded-full bg-white/20">
                  <div className="h-2.5 rounded-full bg-amber-300 transition-all" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-sm font-bold">%{pct}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-2xl bg-slate-200/60" />
          ))}
        </div>
      ) : !plan || !plan.days?.length ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <BookMarked className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 font-semibold text-slate-700">Bu hafta için henüz plan oluşturulmadı</p>
          <p className="text-sm text-slate-500">Yönetici plan eklediğinde burada görünecek.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {normalizeDays(plan.days).map((d) => (
            <DayCard key={d.day} day={d} />
          ))}
        </div>
      )}
    </div>
  );
}

function DayCard({ day }) {
  const empty = !day.entries?.length;
  return (
    <div
      data-testid={`weekly-day-card-${day.day.toLowerCase()}`}
      className="animate-pop-in rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-xl font-bold text-slate-900">{day.day}</h3>
          {day.label && (
            <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-[11px] font-bold text-indigo-700">{day.label}</span>
          )}
        </div>
        {day.date && <span className="font-mono text-sm text-slate-400">{day.date}</span>}
      </div>

      {empty ? (
        <p className="rounded-xl bg-slate-50 py-6 text-center text-sm text-slate-400">Bugün için görev yok</p>
      ) : (
        <div className="space-y-3">
          {day.entries.map((e, i) => {
            const c = colorOf(e.color);
            return (
              <div key={i} className={`rounded-xl border ${c.border} ${c.bg} p-3.5`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className={`flex items-center gap-2 text-sm font-bold ${c.text}`}>
                      <span className={`h-2 w-2 rounded-full ${c.dot}`} /> {e.subject_name}
                    </div>
                    <div className="mt-1 font-semibold text-slate-800">{e.topic || "—"}</div>
                    {e.note && <div className="mt-1 text-xs text-slate-500">📌 {e.note}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`whitespace-nowrap rounded-full ${c.solid} px-2.5 py-1 text-xs font-bold text-white`}>
                      {e.question_count} soru
                    </span>
                    {e.completed ? (
                      <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
                        <CheckCircle2 className="h-4 w-4" /> Tamamlandı
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <Circle className="h-4 w-4" /> Bekliyor
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
